import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { ReactionRowProps } from '@shared/scenes/chat/thread/components/reaction/ReactionRow';
import { ReactionRowWithReactionList } from '@shared/scenes/chat/thread/components/reaction/ReactionRowWithReactionList';
import { SeenBy } from '@shared/scenes/chat/thread/components/seenBy/SeenBy';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';

export interface MBReactionsAndSeenByProps {
  message: API.ChatMessageFragment;
  layout: MBChatMessageLayoutOptions;
  align: 'left' | 'right';
  isDM: boolean;
  selected: API.ReactionType[];
  onToggleReaction: ReactionRowProps['onToggleReaction'];
  marginTop?: number;
}

/*
    Reactions and list of users who have seen the message. This is placed below
    a message bubble. If there are no reactions an no one has seen the message,
    nothing is returned.
*/
export function MBReactionsAndSeenBy({
  message,
  layout,
  align,
  onToggleReaction,
  selected,
  marginTop = 0.5,
  isDM,
}: MBReactionsAndSeenByProps) {
  // we show "(edited)" when the message has been edited, unless this is the last
  // message and has yet to be seen (i.e. we already prepend the "(edited)" text to "Delivered"
  // for when 0 users have seen the message, so no need to show it again here);
  // checking for !!layout.seenByUsers.length also alows for back-to-back unseen messages
  // to display "(edited)" as well
  const isEditedVisible =
    !!message.editedAt && (!layout.isUsersLastSentMessage || !!layout.seenByUsers.length);

  const isSeenByVisible = !layout.isGroupedWithBelow && !!layout.seenByUsers.length;

  // This component has a slight top padding,
  // so we only render it if there is any content
  if (!message.meta.reactions.length && !isEditedVisible && !isSeenByVisible) {
    return null;
  }

  return (
    <>
      <View paddingHorizontal={1} marginTop={marginTop}>
        <ReactionRowWithReactionList
          messageId={message.id}
          selected={selected}
          align={align}
          reactions={message.meta.reactions.filter(notNil)}
          onToggleReaction={onToggleReaction}
        />
      </View>
      <View
        row
        centerVertically
        paddingRight={align === 'right' ? 1.75 : 0}
        paddingLeft={align === 'left' ? 1.75 : 0}
      >
        {isEditedVisible && (
          <Text style={{ fontSize: 10, marginRight: grid(1) }} inactive>
            (edited)
          </Text>
        )}

        {/* we show images only if the message is the last one */}
        {isSeenByVisible && (
          <SeenBy
            align={align}
            users={layout.seenByUsers}
            sender={message.sender}
            showImages={layout.isLastMessage}
            isSeenByCountHidden={isDM}
          />
        )}
      </View>
    </>
  );
}
